import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { useUserAuthStore } from '../../stores/global/userAuth';
import { useAtributoSistemaStore } from '../../stores/administracao/atributosSistema';
import { UserAuthContext } from '../../common/contexts/user_auth_context/user_auth_context';
import { Badge, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Popover } from '@mui/material';
import { useNotificationsStore } from '../../stores/global/notifications';
import { PageContentProvider } from '../../common/contexts/page_content_context';
import { useBackOrNavigate } from '../../common/utils/navigateUtils';
import { AppContext } from '../../common/contexts/app_context/app_context';

import NavBarItems from './NavBarItems';
import DynamicIcon from './DynamicIcon';

const notificationActionClassName = 'inline-flex w-16 h-5 shrink-0 items-center justify-center bg-pink-300 hover:bg-pink-400 text-white font-bold cursor-pointer text-[0.67rem] rounded transition-colors';

const OutubroRosaRibbonIcon = ({ className }: any) => (
  <svg viewBox="0 0 100 120" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Estrutura do laço com cor rosa-400 (pink-400) e rosa-500 (pink-500) */}
    <path d="M30,110 C30,110 70,50 70,30 C70,10 30,10 30,30 C30,50 70,110 70,110" stroke="#F472B6" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function NavBar({ children }: { children: JSX.Element }) {

  const backOrNavigate = useBackOrNavigate();

  // Mocks de contexto/estado para exemplo
  /*
  const [navbarIsCollapsed, setNavbarIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');
  */

  const { navbarIsCollapsed, setNavbarIsCollapsed, isMobileMenuOpen, setIsMobileMenuOpen, toggleTheme, theme } = useContext(AppContext)
  const { nomeEmpresa, logoEmpresa } = useAtributoSistemaStore()

  const [isTestEnvironment, setIsTestEnvironment] = useState(false);
  const [isDevelEnvironment, setIsDevelEnvironment] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const { logout } = useContext(UserAuthContext)
  const { nomeUsuario } = useUserAuthStore()

  const toggleLeftBar = () => {
    setIsMobileMenuOpen(navbarIsCollapsed)
    setNavbarIsCollapsed(!navbarIsCollapsed)
  }

  useLayoutEffect(() => {
    const isTesteEnvironmentFlag = localStorage.getItem('isTestEnvironment') === 'true';
    const isDevelEnvironmentFlag = localStorage.getItem('isDevelEnvironment') === 'true';
    setIsTestEnvironment(isTesteEnvironmentFlag);
    setIsDevelEnvironment(isDevelEnvironmentFlag);
  }, []);

  const notifications = useNotificationsStore((state) => state.notifications);
  const readIds = useNotificationsStore((state) => state.readIds);
  const markNotificationAsRead = useNotificationsStore((state) => state.markAsRead);
  const finalizeNotification = useNotificationsStore((state) => state.finalize);
  const finalizeAllNotifications = useNotificationsStore((state) => state.finalizeAll);
  const unreadCount = notifications.filter((notification) => !readIds.includes(notification.id)).length;

  const anchorNot = useRef<HTMLDivElement>(null);
  const [popover, setPopover] = useState(false);

  return (
    <div className="w-screen max-w-screen h-screen flex flex-col">
      <div
        id="topBar"
        // Gradiente atualizado para Rosa Pastel: pink-200 -> pink-300 -> pink-400
        className="fixed top-0 left-0 w-full h-[4rem] bg-gradient-to-r from-pink-200 via-pink-300 to-pink-400 shadow-md flex flex-row items-center justify-between px-4 z-40 transition-all duration-200 border-none">

        <div className="size-full flex flex-row justify-start items-center">
          {navbarIsCollapsed === false && (
            <IconButton onClick={toggleLeftBar}>
              {/* Mantendo ícones brancos para contraste sobre o rosa pastel */}
              <DynamicIcon nameIcon="MenuOpenRounded" size={30} className="text-white" />
            </IconButton>
          )}
          {navbarIsCollapsed === true && (
            <IconButton onClick={toggleLeftBar}>
              <DynamicIcon nameIcon="MenuRounded" size={30} className="text-white" />
            </IconButton>
          )}
        </div>

        <div className="size-full flex flex-row justify-center items-center">
          <div className="flex gap-3 items-center">
            {/* Novo ícone do laço rosa */}
            <OutubroRosaRibbonIcon className="h-6 w-auto" />
            <div className="h-[3.35rem] w-[5.58rem] bg-white p-[0.15rem] border rounded-lg flex items-center justify-center border-transparent shadow-sm overflow-hidden">
              <img src={logoEmpresa} alt="Logo" className="h-full w-full object-contain" />
            </div>
            {/* Texto branco para contraste */}
            <h1 className="text-white font-bold hidden md:block uppercase tracking-wide drop-shadow-sm">
              {nomeEmpresa}
            </h1>
          </div>
        </div>

        <div className="size-full flex flex-row justify-end items-center">
          <div className="flex flex-row gap-3 items-center">
            {isTestEnvironment && (
              <div className="h-[2.0rem] flex flex-col justify-center items-center px-[0.35rem] py-[0.10rem] border border-white rounded-md bg-red-500/90 shadow-sm">
                <span className="text-white font-bold text-sm">GDOLTeste</span>
              </div>
            )}
            {isDevelEnvironment && (
              <div className="h-[2.0rem] flex flex-col justify-center items-center px-[0.35rem] py-[0.10rem] border border-white rounded-md bg-orange-500/90 shadow-sm">
                <span className="text-white font-bold text-sm">Desenvolvimento</span>
              </div>
            )}
            <IconButton
              aria-describedby="notification-popover"
              onClick={() => {
                // @ts-ignore
                if (window.openZeWidget) window.openZeWidget();
              }}
            >
              <div className="flex flex-row gap-1 items-center">
                <span className="text-white text-sm font-bold drop-shadow-sm">Ajuda</span>
                <DynamicIcon nameIcon="Help" size={28} className="text-white drop-shadow-sm" />
              </div>
            </IconButton>

            <div ref={anchorNot}>
              <IconButton
                aria-describedby="notification-popover"
                onClick={() => {
                  setPopover((prev) => !prev);
                }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <DynamicIcon nameIcon="Notifications" size={28} className="text-white drop-shadow-sm" />
                </Badge>
              </IconButton>
              <Popover
                id="notification-popover"
                open={popover}
                onClose={() => setPopover(false)}
                sx={{ marginTop: 2 }}
                anchorEl={anchorNot.current}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <div className="px-4 py-2 w-[35rem] bg-white dark:bg-gray-800 rounded">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h1 className="font-medium text-lg text-gray-900 dark:text-gray-200">Notificações</h1>
                  </div>
                  {notifications.length > 0 ? (
                    notifications.map((not) => (
                      // Borda laranja -> Rosa Pastel Médio (pink-400)
                      <div key={not.id} className="w-full flex flex-row gap-2 border-t-pink-400 border-t-[1px] mb-2">
                        <div
                          id='notification_container'
                          className={`w-full py-2 rounded`}>
                          {/* Hover laranja -> Rosa Médio (pink-600) para destaque sobre pastel */}
                          <div className='w-full flex flex-col gap-2 flex-1'>
                            <h2 className={`transition-colors ${readIds.includes(not.id)
                              ? 'font-normal text-gray-400 dark:text-gray-400'
                              : 'font-semibold text-pink-700 dark:text-pink-300'}`}>
                              {not.title}
                            </h2>
                            <p className={`text-sm transition-colors ${readIds.includes(not.id)
                              ? 'text-gray-400 dark:text-gray-400'
                              : 'text-gray-600 dark:text-gray-300'}`}>
                              {not.message}
                            </p>
                          </div>
                        </div>

                        <div className='w-24 shrink-0 self-stretch flex flex-col gap-2 justify-between items-end pb-1'>
                          <div className='w-[2rem] h-[2rem]'>
                            <IconButton
                              size="small"
                              aria-label="Finalizar notificação"
                              title="Finalizar notificação"
                              onClick={(event) => {
                                event.stopPropagation();
                                finalizeNotification(not.id);
                              }}>
                              <DynamicIcon nameIcon="Close" size={18} className="text-gray-500 dark:text-gray-300" />
                            </IconButton>
                          </div>
                          {not.navigate?.trim() && !not.linkDownload?.trim() && (
                            <button
                              type="button"
                              className={notificationActionClassName}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (!not.navigate?.trim()) return;
                                markNotificationAsRead(not.id);
                                backOrNavigate(not.navigate.trim());
                                setPopover(false);
                              }}
                            >
                              Ir
                            </button>
                          )}
                          {not.linkDownload?.trim() && (
                            <a
                              href={not.linkDownload}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              className={notificationActionClassName}
                              onClick={(event) => {
                                event.stopPropagation();
                                markNotificationAsRead(not.id);
                              }}
                            >
                              Download
                            </a>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Nenhuma notificação disponível.</p>
                  )}
                  <div className="w-full flex justify-end items-center py-2 border-t-pink-400 border-t-[1px] mt-2">
                    <button
                      type="button"
                      disabled={notifications.length === 0}
                      className="bg-pink-300 hover:bg-pink-400 text-white font-bold text-xs rounded py-1 px-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={finalizeAllNotifications}
                    >
                      Limpar notificações
                    </button>
                    {/* Botão verde -> Rosa Pastel Destaque (pink-300 -> pink-400 no hover) */}
                    {/*
                    <span className='bg-pink-300 hover:bg-pink-400 text-white font-bold cursor-pointer text-xs rounded py-1 px-2 transition-colors'>
                      Tarefas pendentes
                    </span>
                    */}
                  </div>
                </div>
              </Popover>
            </div>
            <IconButton onClick={handleClick}>
              <DynamicIcon nameIcon="AccountCircleOutlined" size={28} className="text-white drop-shadow-sm" />
            </IconButton>
          </div>
        </div>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                width: 220,
              },
            },
          }}>
          <MenuItem>
            <ListItemIcon>
              <DynamicIcon nameIcon="AccountCircleOutlined" size={25} className="text-gray-700 dark:text-white" />
            </ListItemIcon>
            <ListItemText>{nomeUsuario}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => toggleTheme()}>
            <ListItemIcon>
              <DynamicIcon
                nameIcon={theme === "dark" ? "LightModeRounded" : "DarkModeRounded"}
                size={25}
                className={`${theme === "dark" ? "text-yellow-400" : "text-gray-700"}`}
              />
            </ListItemIcon>
            <ListItemText>{theme === "dark" ? "Modo claro" : "Modo escuro"}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => logout()}>
            <ListItemIcon>
              {/* Mantendo o ícone vermelho para semântica de saída */}
              <DynamicIcon nameIcon="LogoutOutlined" size={25} className="text-red-500" />
            </ListItemIcon>
            <ListItemText className="text-red-500">Sair</ListItemText>
          </MenuItem>
        </Menu>
      </div>

      <div
        id="leftBar"
        className={`fixed top-[4rem] left-0 ${navbarIsCollapsed ? 'w-[4rem]' : 'w-[16rem]'
          } h-[calc(100vh-4rem)] bg-light-mode-2 dark:bg-dark-mode-2 border-r-[1px] border-r-border-light-mode dark:border-r-border-dark-mode z-40 transition-all duration-200
        ${isMobileMenuOpen ? 'block' : 'hidden'} md:block`}>
        <NavBarItems isCollapsed={navbarIsCollapsed} setIsCollapsed={setNavbarIsCollapsed} />
      </div>

      <div
        id="conteudo"
        className={`fixed h-[calc(100vh)] top-[4rem] ${navbarIsCollapsed ? 'w-[calc(100vw-4rem)] left-[4rem]' : 'w-[calc(100vw-16rem)] left-[16rem]'} bg-light-mode-1 dark:bg-dark-mode-1 transition-all duration-200 pb-8`}>
        <PageContentProvider>
          {children}
        </PageContentProvider>
      </div>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={toggleLeftBar}
        ></div>
      )}
    </div>
  )
}

export default NavBar
